import { useMemo, useState } from 'react';
import { navigate } from '@/ui/router';
import { Banner } from '@/ui/components/bits';
import { countSigFigs, roundToSigFigs } from '@/domain/sigfigs';

/** The standalone tool: type a number, see its figures and how it rounds. */
export function SigFigToolScreen() {
  const [input, setInput] = useState('0.004560');

  const analysis = useMemo(() => {
    try {
      return { ok: true as const, result: countSigFigs(input) };
    } catch (err) {
      return { ok: false as const, message: (err as Error).message };
    }
  }, [input]);

  return (
    <div className="stack">
      <div className="spread">
        <h1>Sig fig tool</h1>
        <button className="btn btn--sm" onClick={() => navigate('/problems')}>
          Practice problems
        </button>
      </div>

      <label className="label" htmlFor="sigfig-input">
        A number
      </label>
      <input
        id="sigfig-input"
        className="input mono"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="0.004560, 1.20e3, 100"
      />

      {!analysis.ok ? (
        <Banner kind="error">{analysis.message}</Banner>
      ) : (
        <>
          <div className="card stack">
            <div className="spread">
              <span className="faint">Significant figures</span>
              <span className="score" style={{ fontSize: '2rem' }}>
                {analysis.result.count}
              </span>
            </div>

            {analysis.result.ambiguous ? (
              <Banner kind="warn">
                A whole number with trailing zeros is ambiguous — there is no way to tell a
                significant zero from a placeholder. It is counted as {analysis.result.count} here.
                Write it with a decimal point or in scientific notation to say what you mean.
              </Banner>
            ) : null}

            <div className="spread">
              <span className="faint">Value</span>
              <span className="mono">{analysis.result.value}</span>
            </div>
            <div className="spread">
              <span className="faint">Decimal places</span>
              <span className="mono">{analysis.result.decimals}</span>
            </div>
            {analysis.result.units ? (
              <div className="spread">
                <span className="faint">Units read</span>
                <span className="mono">{analysis.result.units}</span>
              </div>
            ) : null}
          </div>

          <h2 className="section-title">Rounded to</h2>
          <ul className="list">
            {[1, 2, 3, 4, 5, 6].map((figures) => {
              let rounded: string;
              try {
                rounded = roundToSigFigs(analysis.result.value, figures);
              } catch {
                rounded = '—';
              }
              return (
                <li key={figures} className="card">
                  <div className="spread">
                    <span className="faint">
                      {figures} figure{figures === 1 ? '' : 's'}
                    </span>
                    <span className="mono">{rounded}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <h2 className="section-title">The rules</h2>
      <div className="card stack">
        <p style={{ margin: 0 }}>Leading zeros never count: 0.00450 has three.</p>
        <p style={{ margin: 0 }}>Zeros between digits always count: 1005 has four.</p>
        <p style={{ margin: 0 }}>
          Trailing zeros count only with a decimal point or in scientific notation: 1.50 has three,
          150 is ambiguous, 1.50e2 has three.
        </p>
        <p style={{ margin: 0 }}>
          Multiplying and dividing: the answer takes the fewest significant figures of the inputs.
          Adding and subtracting: the fewest decimal places. Exact numbers — defined conversions,
          counted objects, atomic masses from your table — never limit anything.
        </p>
      </div>
    </div>
  );
}
